import { SchoolBranding } from "types/api";

type RgbColor = {
  blue: number;
  green: number;
  red: number;
};

export type SchoolThemeVariableMap = Record<string, string>;

const CLASSIC_PRIMARY = "#d3a63c";
const CLASSIC_SECONDARY = "#14191f";
const CLASSIC_HIGHLIGHT = "#f0bf56";
const CLASSIC_INK = "#f5efe6";
const CLASSIC_MUTED = "#c4bfb6";

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const normalizeHexColor = (value?: string | null) => {
  if (!value) {
    return null;
  }

  const trimmed = value.trim();
  const withPrefix = trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
  const shortHex = /^#([0-9a-f]{3})$/i;
  const longHex = /^#([0-9a-f]{6})$/i;

  if (shortHex.test(withPrefix)) {
    return `#${withPrefix
      .slice(1)
      .split("")
      .map((character) => `${character}${character}`)
      .join("")
      .toLowerCase()}`;
  }

  if (longHex.test(withPrefix)) {
    return withPrefix.toLowerCase();
  }

  return null;
};

const hexToRgb = (value: string): RgbColor => {
  const normalized = normalizeHexColor(value) || CLASSIC_PRIMARY;

  return {
    red: Number.parseInt(normalized.slice(1, 3), 16),
    green: Number.parseInt(normalized.slice(3, 5), 16),
    blue: Number.parseInt(normalized.slice(5, 7), 16),
  };
};

const rgbToHex = ({ blue, green, red }: RgbColor) =>
  `#${[red, green, blue]
    .map((channel) => clamp(Math.round(channel), 0, 255).toString(16).padStart(2, "0"))
    .join("")}`;

const mixHexColors = (left: string, right: string, weightOfRight: number) => {
  const ratio = clamp(weightOfRight, 0, 1);
  const leftRgb = hexToRgb(left);
  const rightRgb = hexToRgb(right);

  return rgbToHex({
    red: leftRgb.red * (1 - ratio) + rightRgb.red * ratio,
    green: leftRgb.green * (1 - ratio) + rightRgb.green * ratio,
    blue: leftRgb.blue * (1 - ratio) + rightRgb.blue * ratio,
  });
};

const withAlpha = (hex: string, alpha: number) => {
  const { blue, green, red } = hexToRgb(hex);

  return `rgba(${red}, ${green}, ${blue}, ${clamp(alpha, 0, 1)})`;
};

const luminanceChannel = (channel: number) => {
  const value = channel / 255;
  return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
};

const getRelativeLuminance = (hex: string) => {
  const { blue, green, red } = hexToRgb(hex);

  return (
    0.2126 * luminanceChannel(red) +
    0.7152 * luminanceChannel(green) +
    0.0722 * luminanceChannel(blue)
  );
};

const darkenUntilReadable = (hex: string, maxLuminance = 0.17) => {
  let current = normalizeHexColor(hex) || CLASSIC_SECONDARY;
  let attempts = 0;

  while (getRelativeLuminance(current) > maxLuminance && attempts < 8) {
    current = mixHexColors(current, "#0f172a", 0.22);
    attempts += 1;
  }

  return current;
};

const hasBrandPalette = (branding?: SchoolBranding | null) =>
  Boolean(
    normalizeHexColor(branding?.primary_color) ||
      normalizeHexColor(branding?.secondary_color) ||
      normalizeHexColor(branding?.accent_color),
  );

export const buildSchoolThemeVariables = (
  branding?: SchoolBranding | null,
): SchoolThemeVariableMap | null => {
  if (!hasBrandPalette(branding)) {
    return null;
  }

  const themeMode = String(branding?.theme_mode || "light").toLowerCase();
  const isAccessibleMood = themeMode === "accessible";
  const primary = normalizeHexColor(branding?.primary_color) || CLASSIC_PRIMARY;
  const secondaryBase = normalizeHexColor(branding?.secondary_color) || CLASSIC_SECONDARY;
  const accentSource = normalizeHexColor(branding?.accent_color) || CLASSIC_HIGHLIGHT;
  const highlight = mixHexColors(CLASSIC_HIGHLIGHT, primary, 0.14);
  const accent = mixHexColors(highlight, accentSource, 0.24);
  const accentDeep = darkenUntilReadable(mixHexColors(accent, "#2f210c", 0.62), 0.07);
  const ink = mixHexColors(CLASSIC_INK, "#ffffff", isAccessibleMood ? 0.04 : 0);
  const muted = mixHexColors(ink, CLASSIC_MUTED, isAccessibleMood ? 0.18 : 0.28);
  const bg = darkenUntilReadable(mixHexColors(secondaryBase, "#05070a", 0.34), 0.04);
  const bgStrong = darkenUntilReadable(mixHexColors(bg, "#020304", 0.26), 0.03);
  const shellBackground = mixHexColors(bg, bgStrong, 0.34);
  const panelTop = mixHexColors(secondaryBase, "#282f37", 0.34);
  const panelBottom = mixHexColors(bgStrong, "#0c1015", 0.18);
  const sidebarTop = darkenUntilReadable(mixHexColors(secondaryBase, "#171c22", 0.18), 0.05);
  const sidebarMid = darkenUntilReadable(mixHexColors(sidebarTop, primary, 0.05), 0.06);
  const sidebarBottom = darkenUntilReadable(mixHexColors(bgStrong, "#080b10", 0.18), 0.04);

  return {
    "--brand-bg": bg,
    "--brand-bg-strong": bgStrong,
    "--brand-panel": withAlpha(mixHexColors(panelTop, "#0c1015", 0.16), 0.96),
    "--brand-panel-muted": withAlpha(mixHexColors(panelBottom, accentDeep, 0.04), 0.94),
    "--brand-border": withAlpha("#ffffff", 0.02),
    "--brand-ink": ink,
    "--brand-muted": muted,
    "--brand-accent": accent,
    "--brand-accent-deep": accentDeep,
    "--brand-highlight": highlight,
    "--brand-cool": "#78a7de",
    "--brand-cool-soft": "rgba(120, 167, 222, 0.12)",
    "--brand-cool-faint": "rgba(120, 167, 222, 0.06)",
    "--brand-shadow": "none",
    "--brand-scroll-track": withAlpha("#ffffff", 0.03),
    "--brand-scroll-thumb": mixHexColors(highlight, "#d7aa48", 0.34),
    "--brand-scroll-thumb-hover": highlight,
    "--brand-scroll-thumb-active": mixHexColors(highlight, accentDeep, 0.38),
    "--brand-page-background": shellBackground,
    "--brand-page-glow": "none",
    "--brand-shell-background": shellBackground,
    "--brand-sidebar-background": mixHexColors(
      mixHexColors(sidebarTop, sidebarMid, 0.5),
      sidebarBottom,
      0.48,
    ),
    "--brand-sidebar-border": "transparent",
    "--brand-sidebar-shadow": "none",
    "--brand-button-shadow": "none",
    "--brand-input-border": withAlpha("#ffffff", 0.03),
    "--brand-input-focus-border": withAlpha(highlight, 0.58),
    "--brand-input-focus-ring": `0 0 0 4px ${withAlpha("#78a7de", 0.1)}`,
    "--brand-profile-shadow": "none",
    "--brand-dropdown-shadow": "none",
    "--brand-hero-surface": withAlpha("#78a7de", 0.05),
  };
};

const SAMPLE_SCHOOL_BRANDING: SchoolBranding = {
  accent_color: CLASSIC_HIGHLIGHT,
  primary_color: CLASSIC_PRIMARY,
  secondary_color: CLASSIC_SECONDARY,
};

const SCHOOL_BRAND_THEME_VARIABLE_KEYS = Object.keys(
  buildSchoolThemeVariables(SAMPLE_SCHOOL_BRANDING) || {},
);

const WORKSPACE_SCHOOL_DETAIL_PREFIXES = [
  "/workspace/school-teams/",
  "/workspace/school-players/",
  "/workspace/school-rosters/",
] as const;

const cleanWorkspacePath = (pathname: string) =>
  pathname.split("?")[0].split("#")[0] || "";

export const getSchoolIdFromWorkspacePath = (pathname: string): string | null => {
  const cleanedPath = cleanWorkspacePath(pathname);

  for (const prefix of WORKSPACE_SCHOOL_DETAIL_PREFIXES) {
    if (!cleanedPath.startsWith(prefix)) {
      continue;
    }

    const schoolId = cleanedPath.slice(prefix.length).split("/")[0];
    return schoolId || null;
  }

  return null;
};

/** When the URL locks school context to an entity, changing school should leave that entity. */
export const getWorkspaceSchoolEntityExitPath = (pathname: string): string | null => {
  const cleanedPath = cleanWorkspacePath(pathname);

  if (cleanedPath.startsWith("/workspace/school-teams/")) {
    return "/workspace/school-teams";
  }

  if (cleanedPath.startsWith("/workspace/school-players/")) {
    return "/workspace/school-players";
  }

  if (cleanedPath.startsWith("/workspace/school-rosters/")) {
    return "/workspace/schedule";
  }

  return null;
};

/** Team id from `/workspace/school-teams/:schoolId/:id`. */
export const getTeamIdFromWorkspacePath = (pathname: string): string | null => {
  const cleanedPath = cleanWorkspacePath(pathname);
  const prefix = "/workspace/school-teams/";

  if (!cleanedPath.startsWith(prefix)) {
    return null;
  }

  const [, teamId] = cleanedPath.slice(prefix.length).split("/");
  return teamId || null;
};

/** Player id from `/workspace/school-players/:schoolId/:id`. */
export const getPlayerIdFromWorkspacePath = (pathname: string): string | null => {
  const cleanedPath = cleanWorkspacePath(pathname);
  const prefix = "/workspace/school-players/";

  if (!cleanedPath.startsWith(prefix)) {
    return null;
  }

  const [, playerId] = cleanedPath.slice(prefix.length).split("/");
  return playerId || null;
};

export const clearSchoolBrandThemeOverrides = (root: HTMLElement = document.documentElement) => {
  SCHOOL_BRAND_THEME_VARIABLE_KEYS.forEach((name) => {
    root.style.removeProperty(name);
  });
};
